/**
 * share-identify-form.tsx — name + email capture for guest access.
 *
 * Spec §"decision 7 — guest identify timing":
 *   - Multi-item shares  → prompt at ENTRY (this form gates the landing page).
 *   - Single-asset shares → skip; capture at first-comment instead (Chunk B).
 *
 * The workspace equivalent is inline in watch.tsx; keeping this one
 * standalone since it's reused across share landing + folder browse + watch.
 *
 * On successful submit, the server sets a 24h visitor_id cookie. The parent
 * hook (useShareIdentify) invalidates the landing query so `skipForm` flips
 * to true on the next render.
 */

import { useState, type FormEvent } from "react";
import { useShareIdentify } from "@/hooks/use-share";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authErrorMessage } from "@/lib/auth-errors";

type Props = {
  token: string;
  adminName?: string | null;
  /** Optional callback after identify succeeds — parent uses to close a modal, etc. */
  onIdentified?: () => void;
};

export function ShareIdentifyForm({ token, adminName, onIdentified }: Props) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const identify = useShareIdentify(token);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const cleanName = name.trim();
    const cleanEmail = email.trim();
    if (!cleanName || !cleanEmail) return;
    await identify.mutateAsync({ name: cleanName, email: cleanEmail });
    onIdentified?.();
  }

  return (
    <form onSubmit={handleSubmit} className="mx-auto w-full max-w-sm space-y-4">
      <div className="text-center">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">
          Shared review
        </div>
        <h2 className="mt-2 font-display text-2xl font-bold tracking-tight">
          {adminName ? `${adminName} shared this with you` : "Someone shared this with you"}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Tell us who you are so your comments get attributed.
        </p>
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="identify-name" className="text-xs uppercase tracking-widest text-muted-foreground">
          Name
        </Label>
        <Input
          id="identify-name"
          autoFocus
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Jane Doe"
          maxLength={100}
        />
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="identify-email" className="text-xs uppercase tracking-widest text-muted-foreground">
          Email
        </Label>
        <Input
          id="identify-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="jane@example.com"
          maxLength={200}
        />
      </div>

      {identify.isError && (
        <p className="text-xs text-destructive">{authErrorMessage(identify.error)}</p>
      )}

      <Button
        type="submit"
        className="w-full"
        disabled={identify.isPending || !name.trim() || !email.trim()}
      >
        {identify.isPending ? "One moment…" : "Continue"}
      </Button>

      <p className="text-center text-xs text-muted-foreground">
        Your details stay with the share owner. No account is created.
      </p>
    </form>
  );
}
